import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { ButtonLink } from '../../../components/Buttons';
import EmptyState from '../../../components/EmptyState';
import { P } from '../../../components/Typography';
import { distances } from '../../../styles/constants';
import styled from 'styled-components/macro';

import { Settlement } from '../types';
import NoData from './NoData';
import { safeDecodeURIComponent } from '../../../helpers/formatters';

const Pad = styled.div`
    padding: ${distances.large};
`;

export interface ListMessageProps extends WithTranslation {
    queryParams: URLSearchParams;
    settlements: Settlement[];
    isLoading: boolean;
    isLoadingNext: boolean;
    accountId: string;
    hasNext: boolean;
}

const getEmptyFilterStringPart = (t: TFunction, locationQueryParams: URLSearchParams) => {
    let entries = [...locationQueryParams.entries()]
        .filter(([key]) => !['limit'].includes(key))
        .filter(([key]) => !['starting_after'].includes(key))
        .filter(([key]) => !['created_at.gte', 'created_at.lte'].includes(key))
        .map(([, value]) => {
            return safeDecodeURIComponent(value);
        });

    const fromDate = locationQueryParams.get('created_at.gte');
    const toDate = locationQueryParams.get('created_at.lte');
    if (fromDate && fromDate === toDate) {
        entries = [fromDate, ...entries];
    } else if (fromDate && toDate) {
        entries = [`${fromDate} – ${toDate}`, ...entries];
    } else if (fromDate || toDate) {
        entries = [`${toDate ? '<=' : '>='} ${toDate || fromDate} `, ...entries];
    }
    return entries.join(', ');
};

const ListMessage = (
    {
        t,
        isLoading,
        accountId,
        queryParams,
        isLoadingNext,
        settlements,
    }: ListMessageProps
) => {
    if (isLoading || isLoadingNext) {
        return null;
    }

    const locationValues = getEmptyFilterStringPart(t, queryParams);
    // The account has no transactions yet
    if (!isLoading && settlements.length === 0 && !locationValues) {
        return <Pad><NoData /></Pad>;
    }

    // Filtering settlements yields an empty result and date filter is set
    if (settlements.length === 0 && locationValues) {
        return (
            <EmptyState>
                <P>
                    <Trans
                        i18nKey={'settlements.list_messages.empty_filtered_list.message'}
                        values={{ locationValues }}
                    >
                        Sorry, we can't find any payouts matching <strong>{locationValues}</strong>.
                    </Trans>
                </P>
                <ButtonLink to={`/${accountId}/payments/payouts`}>
                    {t('settlements.list_messages.empty_filtered_list.clear_search_and_filters')}
                </ButtonLink>
            </EmptyState>
        );
    }

    return null;
};

export default withTranslation()(ListMessage);
